import React, { memo, useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import styles from '../Button.module.less';

import { logging } from '../../../actions/log';
import { setOpenFailSoft } from '../../../actions/layout';
import { PCStoreJsBridge } from '../../../nativeapi';
import { downloadAddSoft } from '../../../actions/downloadTask';
import { logNative } from '../../../utils/utils';
import { aigcAddDownload, aigcShowMessage } from '../../../actions/aigc';

const selectLogInfo = createSelector(
  state => state.log,
  log => log.logInfo,
);

const selectDownloadSofts = createSelector(
  state => state.downloadTask,
  downloadTask => downloadTask.softs,
);

const selectInstalledSoft = createSelector(
  state => state.native,
  native => native.installedSoft,
);

const selectUpgradeSofts = createSelector(
  state => state.native,
  native => native.upgradeSofts,
);

const selectSilentInstSoft = createSelector(
  state => state.native,
  native => native.supportSilentInstSoft,
);

const selectIsEnabledSilent = createSelector(
  state => state.layout,
  layout => layout.isEnabledSilent,
);

export default memo(({ soft, info = {}, logInfo = {} }) => {
  const dispatch = useDispatch();

  const logInfoData = useSelector(selectLogInfo);
  const downloadSofts = useSelector(selectDownloadSofts); // 正在下载
  const upgradeSofts = useSelector(selectUpgradeSofts); // 可更新
  const installedSofts = useSelector(selectInstalledSoft); // 已经安装
  const silentInstSoft = useSelector(selectSilentInstSoft); // 支持快速静默安装的应用列表
  const isEnabledSilent = useSelector(selectIsEnabledSilent); // 是否打开快速静默安装

  const [fondSoft, setFondSoft] = useState(undefined);
  const [upgrade, setUpgrade] = useState(undefined);
  const [silentSoft, setSilentSoft] = useState(undefined);
  const [installed, setInstalled] = useState(undefined);
  const [buttonText, setButtonText] = useState(info.text || '安装');

  // 查询当前是否已经安装
  useEffect(() => {
    if (upgradeSofts) {
      setUpgrade(
        upgradeSofts.find(
          soft_ =>
            soft_.androidType !== '1' &&
            (soft_.softID === soft.softID || soft_.SoftID === soft.softID),
        ),
      );
    }
  }, [soft, upgradeSofts]);

  // 查询当前是否已经安装 并且是否可升级
  useEffect(() => {
    if (installedSofts) {
      setInstalled(
        installedSofts.find(
          soft_ =>
            soft_.androidType !== '1' &&
            (soft_.softID === soft.softID || soft_.SoftID === soft.softID),
        ),
      );
    }
  }, [soft, installedSofts]);

  // 查询是否打开静默安装的开关
  // 查询当前是否已经安装 并且是否可升级
  useEffect(() => {
    if (silentInstSoft && isEnabledSilent) {
      setSilentSoft(silentInstSoft.find(soft_ => soft_.softID === soft.softID));
    }
  }, [soft, silentInstSoft, isEnabledSilent]);

  useEffect(() => {
    if (downloadSofts) {
      const found = downloadSofts.find(
        soft_ =>
          soft_.type === 'pc' &&
          (soft_.pc.SoftID === soft.softID ||
            soft_.pc.SoftID === soft.SoftID ||
            soft_.pc.SoftID === soft.softId),
      );
      setFondSoft(found || undefined);
    }
  }, [soft, downloadSofts]);

  // 更新按钮显示状态
  useEffect(() => {
    let button_Text = info.text || '安装';

    if (upgrade) {
      button_Text = '升级';
    } else if (installed) {
      button_Text = '打开';
    } else if (silentSoft && isEnabledSilent) {
      button_Text = '快装';
    }

    if (buttonText !== button_Text) {
      setButtonText(button_Text);
    }
  }, [dispatch, soft, downloadSofts, upgrade, installed, silentSoft, isEnabledSilent]);

  // 获取生成打桩参数
  const getLogParam2 = useCallback(
    (soft_info, logInfo_para) => {
      const logParam2 = {
        source: `AIGC_${logInfoData.source}`,
        source_p: logInfoData.source_p,
        source_pp: logInfoData.source_pp,
        softid: soft_info.softID,
        softName: soft_info.softName,
        softType: '0', // 安卓(1)还是PC(0)应用
        type: upgrade ? 'u' : 'd', // 更新还是下载
        url: soft_info.downLoadUrl,
      };
      // search_type 1: 热词  2: 联想词  3: 搜索词  4: 联想词 首位应用  5: 热词应用  6: 联想词 非首位应用 10: AI来源
      logParam2.search_type = '10';
      logParam2.search_word = logInfo_para.search_word;
      return logParam2;
    },
    [logInfoData, upgrade],
  );

  // 点击按钮处理
  const handleInstallSoft = useCallback(
    (soft_info, button_Text, logInfo_para, fond_soft) => {
      if (!soft_info.softID) {
        return false;
      }
      const logParam2 = getLogParam2(soft_info, logInfo_para);
      // 获取静默安装参数
      let installparams = null;
      if (isEnabledSilent && silentSoft && silentSoft.softID === soft_info.softID) {
        installparams = {};
        logParam2.type = upgrade ? 'xu' : 'xd';
        if (silentSoft.silentParamDic) {
          installparams.quiet = silentSoft.silentParamDic;
        }
        if (silentSoft.silentDirParamDic) {
          installparams.todir = silentSoft.silentDirParamDic;
        }
      }

      let isNeedLog = false;
      const softInfo = { ...soft_info, logInfo: logParam2, installparams, isClick: 'click' };

      if (button_Text === '打开') {
        logParam2.type = 'o';
        PCStoreJsBridge.openSoftDetermine(installed).then(res => {
          if (res?.AppExist) {
            PCStoreJsBridge.openSoft(installed);
          } else {
            dispatch(
              setOpenFailSoft({ isShow: true, ...logInfo, ...soft, btnStyle: info?.btnStyle }),
            );
          }
        });

        isNeedLog = true;
      } else if (!fond_soft) {
        if (button_Text === '安装' || button_Text === '快装') {
          isNeedLog = true;
          // 添加下载任务
          dispatch(downloadAddSoft(softInfo));
          dispatch(
            aigcAddDownload({
              softInfo: softInfo,
              usertext: logInfo_para.search_word,
            }),
          );
        }
        if (button_Text === '升级') {
          isNeedLog = true;
          // 添加下载任务
          dispatch(downloadAddSoft(softInfo));
          dispatch(
            aigcAddDownload({
              softInfo: {
                ...softInfo,
                isUpdate: true,
                versionOld: installed.displayVersion,
                versionNew: soft.version,
              },
              usertext: logInfo_para.search_word,
            }),
          );
        }
      } else {
        // 正常安装
        dispatch(aigcShowMessage({ isShow: true, content: `${soft.softName}已经在任务列表中！` }));
      }

      // 按钮点击打桩
      if (isNeedLog) {
        dispatch(
          logging({
            category: 'C',
            action: 'Search_Aigc_Install',
            label: '',
            param2: logParam2,
            param3: soft_info.bizInfo || null,
          }),
        );
      }
    },
    [dispatch, installed, upgrade, silentSoft, isEnabledSilent],
  );

  return (
    <>
      <span
        className={styles.buttonAI}
        onClick={e => {
          e.preventDefault();
          handleInstallSoft(soft, buttonText, logInfo, fondSoft);
        }}
      >
        {buttonText}
      </span>
    </>
  );
});
